import { useState } from "react";
import { ArrowRight, ArrowLeft, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitApplication } from "@/lib/api";

const STEPS = [
  { id: 1, label: "Contact" },
  { id: 2, label: "Program" },
  { id: 3, label: "Player Profile" },
];

const initial = {
  full_name: "",
  email: "",
  phone: "",
  training_type: "",
  preferred_date: "",
  preferred_time: "",
  age: "",
  level: "",
  position: "Pitcher",
  current_velocity: "",
  target_velocity: "",
  years_pitching: "",
  goals: "",
  website: "", // honeypot — bots fill this; if filled, server drops the request
};

export default function Funnel() {
  const [step, setStep] = useState(1);
  const [data, setData] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const validateStep = () => {
    if (step === 1) {
      if (!data.full_name.trim()) return "Please enter your full name.";
      if (!data.email.includes("@")) return "Please enter a valid email.";
      if (data.phone.replace(/\D/g, "").length < 7)
        return "Please enter a valid phone number.";
    }
    if (step === 2) {
      if (!data.training_type) return "Pick a training format.";
    }
    if (step === 3) {
      if (!data.age || isNaN(Number(data.age)))
        return "Please enter a valid age.";
      if (!data.level) return "Select your level.";
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) {
      toast.error(err);
      return;
    }
    setStep((s) => Math.min(3, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    const err = validateStep();
    if (err) {
      toast.error(err);
      return;
    }
    try {
      setSubmitting(true);
      await submitApplication({ ...data, age: Number(data.age) });
      setDone(true);
      toast.success("Application received. Coach Bham will reach out soon.");
    } catch (e) {
      console.error(e);
      const status = e?.response?.status;
      if (status === 429) {
        toast.error(
          "Too many submissions from this network. Please try again later."
        );
      } else {
        toast.error("Something went wrong submitting. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="apply"
      data-testid="funnel-section"
      className="relative py-24 md:py-32 px-6 md:px-10 bg-[#0a0a0a] border-t border-[#1a1a1a]"
    >
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="w-8 h-px bg-[#eab308]" />
            <span className="font-condensed uppercase tracking-[0.25em] text-xs text-[#eab308]">
              The Application
            </span>
            <span className="w-8 h-px bg-[#eab308]" />
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            Apply To Train With <span className="text-[#eab308]">Bham.</span>
          </h2>
          <p className="text-neutral-400 mt-4 max-w-xl mx-auto">
            Three quick steps. Spots are limited each cycle so I can give every
            athlete the attention they deserve.
          </p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-between mb-10 max-w-2xl mx-auto">
          {STEPS.map((s, i) => {
            const active = step === s.id;
            const complete = step > s.id || done;
            return (
              <div key={s.id} className="flex items-center flex-1 last:flex-none">
                <div
                  data-testid={`step-indicator-${s.id}`}
                  className={`flex items-center gap-3 ${
                    active ? "text-white" : complete ? "text-[#eab308]" : "text-neutral-500"
                  }`}
                >
                  <div
                    className={`w-9 h-9 flex items-center justify-center border-2 font-display ${
                      active
                        ? "border-[#eab308] bg-[#eab308] text-black"
                        : complete
                        ? "border-[#eab308] text-[#eab308]"
                        : "border-[#2a2a2a]"
                    }`}
                  >
                    {complete ? <Check size={16} /> : s.id}
                  </div>
                  <span className="font-condensed uppercase tracking-[0.2em] text-xs hidden sm:inline">
                    {s.label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className="flex-1 h-px bg-[#2a2a2a] mx-3 sm:mx-5" />
                )}
              </div>
            );
          })}
        </div>

        <div className="bg-[#141414] border border-[#2a2a2a] p-6 md:p-10">
          {/* Honeypot — hidden from real users, bots will fill it */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-10000px",
              top: "auto",
              width: "1px",
              height: "1px",
              overflow: "hidden",
            }}
          >
            <label>
              Website
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={data.website}
                onChange={(e) => set("website", e.target.value)}
              />
            </label>
          </div>

          {done ? (
            <div className="py-10 text-center" data-testid="funnel-success">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#eab308] flex items-center justify-center mb-6">
                <Check className="text-black" size={28} />
              </div>
              <h3 className="font-display text-3xl md:text-4xl mb-3">
                You're In The Pipeline.
              </h3>
              <p className="text-neutral-400 max-w-md mx-auto">
                Application received. I'll personally review it and reach out to
                schedule your assessment within 48 hours.
              </p>
            </div>
          ) : (
            <>
              {step === 1 && (
                <Step title="Let's get connected." subtitle="Step 1 of 3 — Contact info">
                  <Field label="Full Name *">
                    <input
                      data-testid="input-full-name"
                      value={data.full_name}
                      onChange={(e) => set("full_name", e.target.value)}
                      placeholder="John Smith"
                      className={inputCls}
                    />
                  </Field>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Email *">
                      <input
                        data-testid="input-email"
                        type="email"
                        value={data.email}
                        onChange={(e) => set("email", e.target.value)}
                        placeholder="you@email.com"
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Phone *">
                      <input
                        data-testid="input-phone"
                        value={data.phone}
                        onChange={(e) => set("phone", e.target.value)}
                        placeholder="(555) 555-5555"
                        className={inputCls}
                      />
                    </Field>
                  </div>
                </Step>
              )}

              {step === 2 && (
                <Step
                  title="Pick your format."
                  subtitle="Step 2 of 3 — Choose how you want to train"
                >
                  <Field label="Training Format *">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {[
                        { v: "remote", t: "Remote", d: "Train from anywhere" },
                        { v: "in-person", t: "In-Person", d: "Hands-on coaching" },
                        { v: "hybrid", t: "Hybrid", d: "Best of both" },
                      ].map((opt) => {
                        const active = data.training_type === opt.v;
                        return (
                          <button
                            key={opt.v}
                            type="button"
                            data-testid={`training-${opt.v}`}
                            onClick={() => set("training_type", opt.v)}
                            className={`text-left p-4 border transition-all ${
                              active
                                ? "border-[#eab308] bg-[#eab308]/10"
                                : "border-[#2a2a2a] hover:border-[#3a3a3a]"
                            }`}
                          >
                            <div
                              className={`font-display text-xl ${
                                active ? "text-[#eab308]" : "text-white"
                              }`}
                            >
                              {opt.t}
                            </div>
                            <div className="text-xs text-neutral-400 mt-1 font-condensed uppercase tracking-wider">
                              {opt.d}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </Field>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Preferred Start Date">
                      <input
                        data-testid="input-preferred-date"
                        type="date"
                        value={data.preferred_date}
                        onChange={(e) => set("preferred_date", e.target.value)}
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Preferred Time">
                      <select
                        data-testid="input-preferred-time"
                        value={data.preferred_time}
                        onChange={(e) => set("preferred_time", e.target.value)}
                        className={inputCls}
                      >
                        <option value="">Select a time of day</option>
                        <option value="morning">Mornings</option>
                        <option value="afternoon">Afternoons</option>
                        <option value="evening">Evenings</option>
                        <option value="weekend">Weekends</option>
                      </select>
                    </Field>
                  </div>
                </Step>
              )}

              {step === 3 && (
                <Step
                  title="Tell me about your game."
                  subtitle="Step 3 of 3 — Player profile"
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <Field label="Age *">
                      <input
                        data-testid="input-age"
                        type="number"
                        min="10"
                        max="35"
                        value={data.age}
                        onChange={(e) => set("age", e.target.value)}
                        placeholder="17"
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Level *">
                      <select
                        data-testid="input-level"
                        value={data.level}
                        onChange={(e) => set("level", e.target.value)}
                        className={inputCls}
                      >
                        <option value="">Select level</option>
                        <option value="middle-school">Middle School</option>
                        <option value="high-school">High School</option>
                        <option value="college">College</option>
                        <option value="other">Other</option>
                      </select>
                    </Field>
                    <Field label="Position">
                      <input
                        data-testid="input-position"
                        value={data.position}
                        onChange={(e) => set("position", e.target.value)}
                        placeholder="Pitcher"
                        className={inputCls}
                      />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <Field label="Current Velocity (mph)">
                      <input
                        data-testid="input-current-velo"
                        value={data.current_velocity}
                        onChange={(e) => set("current_velocity", e.target.value)}
                        placeholder="78"
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Target Velocity (mph)">
                      <input
                        data-testid="input-target-velo"
                        value={data.target_velocity}
                        onChange={(e) => set("target_velocity", e.target.value)}
                        placeholder="88"
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Years Pitching">
                      <input
                        data-testid="input-years"
                        value={data.years_pitching}
                        onChange={(e) => set("years_pitching", e.target.value)}
                        placeholder="4"
                        className={inputCls}
                      />
                    </Field>
                  </div>

                  <Field label="Goals & Background">
                    <textarea
                      data-testid="input-goals"
                      rows={4}
                      value={data.goals}
                      onChange={(e) => set("goals", e.target.value)}
                      placeholder="What are you trying to accomplish this season? Any injuries, recruiting goals, or specific concerns?"
                      className={`${inputCls} resize-none`}
                    />
                  </Field>
                </Step>
              )}

              <div className="mt-10 flex items-center justify-between gap-4">
                <button
                  data-testid="btn-back"
                  onClick={back}
                  disabled={step === 1}
                  className="inline-flex items-center gap-2 font-condensed uppercase tracking-[0.2em] text-sm text-neutral-300 hover:text-[#eab308] disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ArrowLeft size={16} /> Back
                </button>

                {step < 3 ? (
                  <button
                    data-testid="btn-next"
                    onClick={next}
                    className="inline-flex items-center gap-3 bg-[#eab308] hover:bg-[#facc15] text-black font-bold uppercase font-condensed tracking-widest px-6 py-3 rounded-sm transition-all hover:-translate-y-0.5"
                  >
                    Continue <ArrowRight size={16} />
                  </button>
                ) : (
                  <button
                    data-testid="btn-submit"
                    onClick={submit}
                    disabled={submitting}
                    className="inline-flex items-center gap-3 bg-[#eab308] hover:bg-[#facc15] text-black font-bold uppercase font-condensed tracking-widest px-6 py-3 rounded-sm transition-all hover:-translate-y-0.5 disabled:opacity-60"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Submitting
                      </>
                    ) : (
                      <>
                        Submit Application <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

const inputCls =
  "w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#eab308] focus:ring-2 focus:ring-[#eab308]/30 outline-none px-4 py-3 text-white placeholder:text-neutral-600 rounded-sm transition-all";

function Field({ label, children }) {
  return (
    <label className="block space-y-2 mb-5">
      <span className="font-condensed uppercase tracking-[0.2em] text-xs text-neutral-300">
        {label}
      </span>
      {children}
    </label>
  );
}

function Step({ title, subtitle, children }) {
  return (
    <div className="bham-rise">
      <div className="mb-6">
        <div className="font-condensed uppercase tracking-[0.25em] text-xs text-[#eab308] mb-2">
          {subtitle}
        </div>
        <h3 className="font-display text-2xl md:text-3xl">{title}</h3>
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
